import { useTranslation } from 'react-i18next';

import { Button } from '@arco-design/web-react';
import { IconRefresh } from '@arco-design/web-react/icon';
import styled from 'styled-components';

import { useDataCenterSwitcherPlacement } from '@/hooks/useTenantDataCenterSwitcher';

import { ListDisplayType, ListSwitcher } from '../ListSwitcher';
import { ChangeDataCenter } from '../TopNav/components/ChangeModule';
import { TagInput } from './components/tag-input';
import type { Resource, TagFilter } from './types';
import { isFilters } from './types';

/**
 * TagFiltersProps 接口定义了 TagFilters 组件的属性。
 */
export interface TagFiltersProps {
  /** 显示类型: 列表/卡片 */
  displayType?: ListDisplayType;
  onChangeDisplayType?: (displayType: ListDisplayType) => void;
  /** 配置项: 定义每一个筛选项的静态参数 */
  filters: TagFilter[];
  /** 外部资源: 定义每个 key 对应的加载项,比如 options  */
  resource?: Resource[];
  /** 对象: 属性 key 需要与 filters 里面配置的 key 对应 */
  values?: Record<string, any>;
  /** 筛选对象变化的回调 */
  onChange?: (values: Record<string, any>) => void;
  /** 输入框文本占位 */
  placeholder?: string;
  /** 搜索框最小宽度（px），默认 260；空间不足时工具栏换行，容器更窄时以容器宽度为限。 */
  searchMinWidth?: number;
  /** 可选的前缀节点: 在输入框之前 */
  prefix?: React.ReactNode;
  /** 可选的后缀节点: 介于输入框和按钮之间 */
  suffix?: React.ReactNode;
  /** 视图切换前的附加控件，位于刷新按钮之后；无视图切换时仍展示。 */
  beforeViewSwitcher?: React.ReactNode;
  /** 可选的额外节点: 在按钮之后 */
  extra?: React.ReactNode;
  /** 刷新操作的回调函数 */
  onRefresh?: () => void;
  /** 可选的样式对象 */
  style?: React.CSSProperties;
  /** Dropdown 挂载容器，未传时默认挂载到 TAG_FILTER_INPUT_BOX */
  getPopupContainer?: (node: HTMLElement) => Element;
  /** 强制隐藏数据中心切换器（弹窗等局部场景不需要切 DC） */
  hideDataCenterSwitcher?: boolean;
}

export function TagFilters({
  displayType,
  onChangeDisplayType,
  filters,
  resource,
  values,
  onChange,
  placeholder,
  searchMinWidth = 260,
  prefix,
  suffix,
  beforeViewSwitcher,
  extra,
  onRefresh,
  style,
  getPopupContainer,
  hideDataCenterSwitcher = false,
}: TagFiltersProps) {
  const { t } = useTranslation();
  const dataCenterPlacement = useDataCenterSwitcherPlacement();
  const showDataCenterSwitcher = !hideDataCenterSwitcher && dataCenterPlacement === 'tag-filters';
  const showRefresh = typeof onRefresh === 'function';
  // 不为 hidden 的配置项存在才展示. 因为 hidden 的配置项不在下拉菜单中展示,如果全是 hidden 或者没有传入 filters, 那么输入框就没意义了
  const showInputWrapper = filters?.filter((f) => isFilters(f) && !f.hidden)?.length > 0;
  // 使用 visibility 来控制显示隐藏, 是为了保持 ui 行为一致性, 即便隐藏, 位置不变
  const visibility = showInputWrapper ? 'visible' : 'hidden';

  return (
    <TagFiltersWrapper style={style}>
      {showDataCenterSwitcher && <ChangeDataCenter style={{ width: 'auto', flex: '0 0 auto', height: '32px' }} refreshCurrentPage />}
      {prefix && <PrefixWrapper>{prefix}</PrefixWrapper>}
      <TagInput
        filters={filters}
        resource={resource}
        values={values}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          visibility,
          flex: `1 1 ${searchMinWidth}px`,
          width: 'auto',
          minWidth: `min(${searchMinWidth}px, 100%)`,
          maxWidth: '100%',
        }}
        getPopupContainer={getPopupContainer}
      />
      <SuffixWrapper>
        {suffix}
        {showRefresh && (
          <Button type="default" icon={<IconRefresh />} onClick={onRefresh}>
            {t('common:refresh')}
          </Button>
        )}
        {beforeViewSwitcher}
        {displayType && onChangeDisplayType && <ListSwitcher displayType={displayType} onChange={onChangeDisplayType} />}
        {extra}
      </SuffixWrapper>
    </TagFiltersWrapper>
  );
}

const Flex = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  min-width: 0;
  max-width: 100%;
  gap: 4px;
`;

const TagFiltersWrapper = styled(Flex)`
  width: 100%;
  row-gap: 8px;
`;

const PrefixWrapper = styled(Flex)``;

const SuffixWrapper = styled(Flex)``;
